import { emptySplitApi } from "./emptyApi"

/**
 * Destination URL audit — where paid clicks actually land.
 *
 * Backed by `GET /cli/destination-audit`, the same endpoint the `destination_audit`
 * MCP tool calls. `/cli/*` accepts either a session cookie or an API key, so the
 * browser and the agent read one implementation rather than two that drift.
 */

export type TAuditSeverity = "error" | "warning" | "info"

export interface TDestinationFinding {
  /** Stable code, e.g. NO_TRACKING, BROKEN_LINK, OFF_BRAND_DOMAIN. */
  code: string
  severity: TAuditSeverity
  message: string
  /** The offending value, when there is one worth showing. */
  snippet?: string | null
}

export interface TDestinationAuditAd {
  adId: string
  adName: string
  campaignName: string
  channel: string
  /** Destination URL on the creative. Empty when the platform reports none. */
  url: string
  /** Effective UTM query string — link params merged with url_tags. */
  tracking: string
  spend: number
  /** Only set when the live check ran. */
  httpStatus?: number | null
  finalUrl?: string | null
  findings: TDestinationFinding[]
}

/** One finding code rolled up across every ad that hit it. */
export interface TDestinationIssue {
  code: string
  title: string
  severity: TAuditSeverity
  adCount: number
  spend: number
  examples: TDestinationAuditAd[]
}

/**
 * One distinct (source, medium) pair in use on a channel. More than one row per
 * channel means the same traffic arrives under different labels and gets split
 * across every downstream analytics report.
 */
export interface TDestinationTrackingVariant {
  channel: string
  source: string
  medium: string
  adCount: number
  spend: number
}

/**
 * A channel whose ads carry no destination and no URL tags at all — Google Ads
 * keeps its tracking in `final_url_suffix`, which the Cube model does not
 * expose. Reported as not-audited rather than audited-and-broken.
 */
export interface TDestinationUncoveredChannel {
  channel: string
  adCount: number
  spend: number
}

export interface TDestinationAuditSummary {
  adsAudited: number
  spendAudited: number
  adsWithFindings: number
  spendWithFindings: number
  errorSpend: number
  distinctUrls: number
  /** Distinct URLs actually fetched. 0 unless the live check ran. */
  urlsChecked: number
  /** Catalog/dynamic ads the platform reports no destination for. Not a fault. */
  adsWithoutDestination: number
  spendWithoutDestination: number
}

export interface TDestinationAuditResponse {
  range: string
  /** Company domain the off-brand check ran against. Empty disables that check. */
  brandDomain: string
  summary: TDestinationAuditSummary
  issues: TDestinationIssue[]
  trackingVariants: TDestinationTrackingVariant[]
  /** Channels with no data available — their spend was NOT audited. */
  uncoveredChannels: TDestinationUncoveredChannel[]
  ads: TDestinationAuditAd[]
}

export interface TDestinationAuditArgs {
  /** Company slug or id. */
  company: string
  range?: string
  channel?: string
  limit?: number
  /** Fetch each destination to check it resolves. Slower — off by default. */
  live?: boolean
}

/**
 * Query string for the audit endpoint. Optional params are omitted rather than
 * sent empty — the API validates `channel` and `limit` when present, so a blank
 * value is a 400 rather than a no-op — and `live` is a string because it
 * crosses the wire as one.
 */
export function buildDestinationAuditParams({
  company,
  range,
  channel,
  limit,
  live,
}: TDestinationAuditArgs): Record<string, string> {
  return {
    company,
    ...(range ? { range } : {}),
    ...(channel ? { channel } : {}),
    ...(limit ? { limit: String(limit) } : {}),
    live: live ? "true" : "false",
  }
}

export const destinationAuditApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getDestinationAudit: builder.query<
      TDestinationAuditResponse,
      TDestinationAuditArgs
    >({
      query: args => ({
        url: "/cli/destination-audit",
        params: buildDestinationAuditParams(args),
      }),
    }),
  }),
})

export const { useGetDestinationAuditQuery } = destinationAuditApi
